/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Injectable, Injector } from '@angular/core';
import { DiConfigService } from '@app/di/di-config.service';
import { AppConfigService } from '@app/common/app-config.service';
import { TranslateService } from '@ngx-translate/core';
import { Logger } from '@core/lib/services';
import { UtilService } from '@app/util/util.service';
import { NotifyService } from '@app/common/notify.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { DiQueue } from '@app/di/data-model/di-queue';
import { DiService } from '@app/di/di.service';
import { WindowRef } from '@app/common/window-ref.service';
import { appConstants } from '@app/common/app-constants';
import { MessageService } from '@core/lib/services/message.service';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { LineShaftIntegration, LineShaftHandlerService } from '../channel/lineshaft/line-shaft-handler.service';

const log = new Logger('AppLauncherService');

@Injectable({
  providedIn: 'root'
})
export class AppLauncherService implements LineShaftIntegration {
  private lineShaftHandlerService: LineShaftHandlerService;
  private AppLauncherMessageQueue = new DiQueue();
  private appLauncherSetContextTimeout = null;
  private cemwindow: Window;
  private APPLAUNCHER_CONFIG;
  private UVIEWER_CONFIG;
  private awSecurity;

  siteName;
  siteConfig;

  constructor(
    private injector: Injector,
    private diConfigService: DiConfigService,
    private appConfigService: AppConfigService,
    private notify: NotifyService,
    private xmlTemplateManagerService: XmlTemplateManagerService,
    private integrationStatusService: IntegrationStatusService,
    private diService: DiService,
    private winRef: WindowRef,
    private translate: TranslateService,
    private messageService: MessageService,
    private userPrivilegeService: UserPrivilegesService
  ) {}

  init() {
    log.info('App Initializing...');
    // Communication channel
    this.lineShaftHandlerService = this.injector.get(LineShaftHandlerService);
    this.lineShaftHandlerService.registerCleanupCallback(this.cleanup);
    this.lineShaftHandlerService.registerCleanupCallback(this.initiateExamManagerClose);

    // Configs
    this.APPLAUNCHER_CONFIG = this.diConfigService.APP_LAUNCHER_CONFIG;
    this.UVIEWER_CONFIG = this.diConfigService.UVIEWER_CONFIG;

    // Listeners
    this.broadcastListeners();
  }

  setAWSecurity(security) {
    this.awSecurity = security;
  }

  subscribePluginResponse() {
    // Subscribe the load response from AppLauncher plugin
    const loadResponse = this.xmlTemplateManagerService.getSubscribeXml(
      'LS_PLUGIN_LOAD_RESPONSE',
      this.APPLAUNCHER_CONFIG.MODULENAME
    );
    log.info('Subscribe the load response from AppLauncherPlugin');
    this.diService.loadPluginsInvokeAPIWrapperExecute(loadResponse);

    // Subscribe response from LineShaftPluginManager to get load response error due to the missing
    // of AppLauncher plugin
    const loadResponseFromPluginManager = this.xmlTemplateManagerService.getSubscribeXml(
      this.APPLAUNCHER_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_LOAD_RESPONSE',
      this.APPLAUNCHER_CONFIG.PLUGIN_MANAGER
    );
    this.diService.loadPluginsInvokeAPIWrapperExecute(loadResponseFromPluginManager);

    // Subscribe the setcontext response from AppLauncherPlugin
    const setcontextResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.APPLAUNCHER_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_SETCONTEXT_RESPONSE',
      this.APPLAUNCHER_CONFIG.MODULENAME
    );
    log.info('Subscribe the setcontext response from AppLauncherPlugin');
    this.diService.loadPluginsInvokeAPIWrapperExecute(setcontextResponse);
  }

  loadPlugin() {
    if (this.userPrivilegeService.hasPrivilege('AW Server')) {
      this.diConfigService.readAWConfiguration().then(
        (data: Map<any, any>) => {
          log.info('AW configuration received : ' + JSON.stringify(data));
          if (data.get('awIntegrationStatus')) {
            this.setAWSecurity(data.get('awSecurity'));
            this.loadAppLauncherPlugin();
          }
        },
        reason => {
          log.error('Error in reading AW configuration, reason : ' + reason.statusText);
        }
      );
    }
  }

  loadAppLauncherPlugin() {
    // Subscribe responses from plugin
    this.subscribePluginResponse();
    log.info('load AppLauncherPlugin');
    // Create and send the AppLauncher load xml
    const appLauncherLoadXML = this.xmlTemplateManagerService.getPluginLoadXml(this.APPLAUNCHER_CONFIG.MODULENAME);
    const appLauncherLoadMsg = this.diService.getLSMessage(this.diService.getLSMessageType().LOAD, appLauncherLoadXML);
    appLauncherLoadMsg.responseSource = [this.APPLAUNCHER_CONFIG.MODULENAME, this.APPLAUNCHER_CONFIG.PLUGIN_MANAGER];
    appLauncherLoadMsg.moduleName = this.APPLAUNCHER_CONFIG.MODULENAME;
    this.integrationStatusService.setAppLauncherPluginStatus(false);
    this.diService.loadPluginsInvokeAPIWrapper(appLauncherLoadMsg);
  }

  onCDImport(siteName, site) {
    if (!this.integrationStatusService.getAppLauncherPluginStatus()) {
      // Keep the site name and site config to invoke the CD import operation on receiving load success response
      this.siteConfig = site;
      this.siteName = siteName;
      this.loadAppLauncherPlugin();
    } else if (
      this.integrationStatusService.getAppLauncherHealthStatus() ===
      this.integrationStatusService.healthStatusEnum.ready
    ) {
      if (!this.validateParameters(site)) {
        log.error('onCDImport - invalid parameters to perform CD Import, site configuration : ' + JSON.stringify(site));
        this.notify.warning(
          this.translate.instant('di.desktopIntegration.warning.AppLauncherInvalidParameters'),
          this.translate.instant('di.desktopIntegration.title')
        );
        return;
      }
      log.info('Send AppLauncher setcontext request');
      this.diConfigService.getJwtToken().then(jwtToken => {
        const cdImportCommandLine =
          this.APPLAUNCHER_CONFIG.PROCESSNAME +
          ' /server=' +
          site.Server +
          ' /port=' +
          site.PortNumber +
          ' /calling_AE=' +
          site.CallingAETitle +
          ' /called_AE=' +
          site.CalledAETitle +
          ' /UVServer=' +
          this.UVIEWER_CONFIG.SERVER +
          ' /language=' +
          this.UVIEWER_CONFIG.LANGUAGE +
          ' /jwt=' +
          jwtToken +
          ' /security=' +
          this.UVIEWER_CONFIG.SECURITY;
        log.debug('CD Import command line parameters : ', cdImportCommandLine);
        const stopCommandLine = '';
        const appLauncherSetContextXML = this.xmlTemplateManagerService.getAppLauncherSetContextXml(
          cdImportCommandLine,
          stopCommandLine,
          siteName
        );
        this.appLauncherInvokeAPIWrapper(appLauncherSetContextXML, siteName);
      });
    }
  }

  launchExamManager() {
    if (!this.cemwindow || this.cemwindow.closed) {
      const cemIP = this.appConfigService.getAppConfigByKey(appConstants.appLauncher.CEM_IP);
      const cemURL =
        appConstants.appLauncher.HTTPS_COLON_2_FWD_SLASHES + cemIP + appConstants.appLauncher.CEM_CONTEXT_PATH;
      this.cemwindow = this.winRef.nativeWindow.open(
        cemURL,
        appConstants.appLauncher.BLANK,
        appConstants.appLauncher.WINDOW_CONFIG
      );
      this.cemwindow.blur();
      this.cemwindow.focus();
    } else {
      this.cemwindow.focus();
    }
  }

  initiateExamManagerClose = () => {
    log.info('CleanUp - Close ExamManager');
    if (this.cemwindow && !this.cemwindow.closed) {
      this.cemwindow.focus();
      this.cemwindow.close();
    }
    return true;
  };

  validateParameters(siteConfig) {
    return (
      siteConfig && siteConfig.Server && siteConfig.PortNumber && siteConfig.CalledAETitle && siteConfig.CalledAETitle
    );
  }

  handleSetcontextResponse(responseInfo) {
    if (responseInfo.resultValue === 'FAILURE') {
      if (responseInfo.destination) {
        log.error(
          'handleSetcontextResponse - AppLauncherPlugin SetContext Failure for destination -' +
            responseInfo.destination +
            ' Failure reason : ' +
            responseInfo.resultMsg
        );
        this.notify.warning(
          this.translate.instant('di.desktopIntegration.warning.AppLauncherSetContextFailed'),
          this.translate.instant('di.desktopIntegration.title')
        );
      } else {
        log.error(
          'handleSetcontextResponse - AppLauncherPlugin SetContext Failure for Solomini, ' +
            'Failure reason : ' +
            responseInfo.resultMsg
        );
      }
    } else {
      log.info('handleSetcontextResponse - AppLauncherPlugin SetContext Success');
    }
  }

  /**
   * The responsibility of this method is
   * 1. Send request to AppLauncherPlugin if no response is pending
   * 2. If any response is pending push the request to AppLauncher message queue
   * @param xml - request xml
   */
  appLauncherInvokeAPIWrapper(xml, siteName?) {
    const lsMessage = this.diService.getLSMessage(this.diService.getLSMessageType().SETCONTEXT, xml);
    lsMessage.destination = siteName;
    if (this.AppLauncherMessageQueue.messageWaitingForResponse === undefined) {
      log.info('calling invokeAPIWrapperExecute from appLauncherInvokeAPIWrapper');
      this.AppLauncherMessageQueue.messageWaitingForResponse = lsMessage;
      this.diService.invokeAPIWrapperExecute(lsMessage.message);
      this.setAppLauncherSetContextTimeout();
    } else {
      // Put the request on queue
      this.AppLauncherMessageQueue.messageQueue.push(lsMessage);
      log.info(
        'appLauncherInvokeAPIWrapper - Add to queue - queue count: ' + this.AppLauncherMessageQueue.messageQueue.length
      );
    }
  }

  /**
   * The responsibility of this method is to:
   * 1. Set timeout for AppLauncher setcontext response
   * 2. Display error message when timeout occurs
   * 3. Process next request from AppLauncher message queue
   */
  setAppLauncherSetContextTimeout() {
    log.debug(
      'setAppLauncherSetContextTimeout : setting timeout for Applauncher setcontext response : ' +
        this.APPLAUNCHER_CONFIG.SETCONTEXT_TIMEOUT
    );
    // Timeout when setcontext response from AppLauncher plugin is pending
    this.appLauncherSetContextTimeout = setTimeout(() => {
      if (this.AppLauncherMessageQueue.messageWaitingForResponse !== undefined) {
        log.info('setAppLauncherSetContextTimeout : timeout occurred while receiving' + 'the AppLauncher response');
        this.notify.error(
          this.translate.instant('di.desktopIntegration.error.AppLauncherResponsePendingOnTimeout'),
          this.translate.instant('di.desktopIntegration.title')
        );
        this.AppLauncherMessageQueue.messageWaitingForResponse = undefined;
        this.processAppLauncherQueue();
      }
    }, this.APPLAUNCHER_CONFIG.SETCONTEXT_TIMEOUT);
  }

  /**
   * The responsibility of this method is to process the AppLauncher requests from queue
   */
  processAppLauncherQueue() {
    if (this.AppLauncherMessageQueue.messageWaitingForResponse === undefined) {
      const lsMessage =
        this.AppLauncherMessageQueue.messageQueue.length > 0 ? this.AppLauncherMessageQueue.messageQueue.shift() : null;
      if (lsMessage !== null) {
        this.AppLauncherMessageQueue.messageWaitingForResponse = lsMessage;
        log.info('calling invokeAPIWrapperExecute from processAppLauncherQueue');
        this.diService.invokeAPIWrapperExecute(lsMessage.message);
        this.setAppLauncherSetContextTimeout();
      } else {
        log.info('processAppLauncherQueue - no message');
      }
    }
  }

  processQueueOnSetcontextResponse(responseInfo) {
    log.info('processQueueOnSetcontextResponse - process next request from AppLauncher queue');
    if (
      _.isUndefined(this.AppLauncherMessageQueue.messageWaitingForResponse.destination) ||
      this.AppLauncherMessageQueue.messageWaitingForResponse.destination === responseInfo.destination
    ) {
      log.info(
        'processQueueOnSetcontextResponse - AppALauncher response received : ' +
          responseInfo.destination +
          '. Process next request from AppLauncher queue.'
      );
      this.AppLauncherMessageQueue.messageWaitingForResponse = undefined;
      // Clear the AppLauncher setcontext timeout
      clearTimeout(this.appLauncherSetContextTimeout);
      this.processAppLauncherQueue();
    } else {
      log.warn(
        'processQueueOnSetcontextResponse - Unexpected response received from AppLauncher - ' +
          'Expected destination: ' +
          this.AppLauncherMessageQueue.messageWaitingForResponse.destination +
          ' Received: ' +
          responseInfo.destination
      );
    }
  }

  /**
   * Function to get the message waiting for response.
   *
   */
  getMessageWaitingForResponse() {
    return this.AppLauncherMessageQueue.messageWaitingForResponse;
  }

  /**
   * Function to prepare command line for Solomini application.
   *
   */
  prepareSolominiCommandLine(operation, jwtToken) {
    let solominiCmdLine = this.APPLAUNCHER_CONFIG.MIVPROCESSNAME + ' /mode=command';
    solominiCmdLine += ' /solomini=' + operation;
    solominiCmdLine += ' /server=' + this.UVIEWER_CONFIG.SERVER;
    solominiCmdLine += ' /UVServer=' + this.UVIEWER_CONFIG.SERVER;
    solominiCmdLine += ' /site= /pipe /compression=8 /quality=2';
    solominiCmdLine += ' /jwt=' + jwtToken;
    solominiCmdLine += ' /version=3.1';
    solominiCmdLine += ' /security=' + this.UVIEWER_CONFIG.SECURITY;
    const awSecurity = this.awSecurity ? '1' : '0';
    solominiCmdLine += ' /awsecurity=' + awSecurity;
    solominiCmdLine += ' /PatientsFolder=always';
    solominiCmdLine += ' /language=' + this.UVIEWER_CONFIG.LANGUAGE;
    const lights = UtilService.lights ? 'on' : 'off';
    solominiCmdLine += ' /lights=' + lights;
    solominiCmdLine += ' /login=' + this.UVIEWER_CONFIG.LOGIN;
    return solominiCmdLine;
  }

  launchSolominiApplication(jwtToken) {
    const solominiStartCmdline = this.prepareSolominiCommandLine('start', jwtToken);
    const solominiStopCmdline = this.prepareSolominiCommandLine('stop', jwtToken);
    const appLauncherSetContextXML = this.xmlTemplateManagerService.getAppLauncherSetContextXml(
      solominiStartCmdline,
      solominiStopCmdline
    );
    this.appLauncherInvokeAPIWrapper(appLauncherSetContextXML);
  }

  invokeSolominiStartup() {
    this.diConfigService.getJwtToken().then(
      jwtToken => {
        this.launchSolominiApplication(jwtToken);
      },
      e => {
        log.error('invokeSolominiStartup getJwtToken fetch failed - ' + e);
      }
    );
  }

  /**
   * Method to handle AppLauncher plugin load response.
   */
  private handleAppLauncherPluginLoadResponse(responseInfo) {
    if (responseInfo.resultValue === 'SUCCESS') {
      if (this.integrationStatusService.getAppLauncherPluginStatus()) {
        log.info('handleAppLauncherPluginLoadResponse : AppLauncher plugin load success after timeout');
        this.notify.success(
          this.translate.instant('di.desktopIntegration.success.loadAppLauncherSuccessAfterTimeout'),
          this.translate.instant('di.desktopIntegration.titleAppLauncherEnabled')
        );
      }
      log.info('handleAppLauncherPluginLoadResponse : AppLauncher plugin load success');
      this.integrationStatusService.setAppLauncherPluginStatus(true);
      this.integrationStatusService.setAppLauncherHealthStatus(this.integrationStatusService.healthStatusEnum.ready);

      // If siteName is valid assume that the plugin is loaded from CD Import operation so no need to start
      // Solomini application, if siteName is undefined assume that the plugin is loaded for Solomini startup
      if (!_.isUndefined(this.siteName)) {
        // calls cd import
        this.onCDImport(this.siteName, this.siteConfig);
      } else {
        // Initiate Solomini startup
        this.invokeSolominiStartup();
      }
    } else {
      log.error(
        'handleAppLauncherPluginLoadResponse - AppLauncher plugin load failed ' +
          '- Failure reason : ' +
          responseInfo.resultMsg
      );
      this.integrationStatusService.setAppLauncherHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
    }
  }

  /**
   * Method to handle AppLauncher plugin load failure from LS plugin manager.
   *
   */
  private handleAppLauncherPluginLoadFailureFromPM() {
    this.displayNotificationMsgDetailsInSystemStatus(
      'keyAppLauncherPluginLoadFail',
      this.translate.instant('systemStatusMsg.appLauncherPluginLoadFail')
    );
    log.error(
      'handleAppLauncherPluginLoadFailureFromPM - AppLauncher plugin load failed and ' +
        'AppLauncher plugin integration disabled'
    );
    if (!this.integrationStatusService.getAppLauncherPluginStatus()) {
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.loadAppLauncherFailed'),
        this.translate.instant('di.desktopIntegration.titleAppLauncherDisabled')
      );
    }
    this.integrationStatusService.setAppLauncherHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
    this.integrationStatusService.setAppLauncherPluginStatus(true);
  }

  private displayNotificationMsgDetailsInSystemStatus(key, message) {
    this.appConfigService.getSystemStatusNotifier().next({ key, message });
  }

  cleanup = (): boolean => {
    try {
      log.info('CleanUp - Started');
      // Close the plugin only if it is loaded
      if (this.integrationStatusService.getAppLauncherPluginStatus()) {
        // During logout LineShaft WebSocket connection is closed without any delay. So even if we send the
        // Terminate Solomini request form here it may not be processed by the Plugin. So removing the Terminate
        // Solomini request request from here, it is already handled in Plugin Close handler which is supposed to
        // execute either from the Close request sending below or from LineShaft WebSocket connection close flow.

        const appLauncherCloseXML = this.xmlTemplateManagerService.getPluginCloseXml(
          this.APPLAUNCHER_CONFIG.MODULENAME
        );
        this.diService.invokeAPIWrapperExecute(appLauncherCloseXML);
        return true;
      }
    } catch (error) {
      log.error('CleanUp - Error >>', error);
      return false;
    }
  };

  triggerProcessQueueOnResponse(responseInfo) {
    // If setcontext response is received process the next request from queue
    if (
      responseInfo.topicName.indexOf('SETCONTEXT_RESPONSE') > 0 ||
      responseInfo.topicName.indexOf('CLOSE_RESPONSE') > 0
    ) {
      log.info(
        'triggerProcessQueueOnResponse - setcontext/close response' + ' received process next request from queue'
      );
      !this.getMessageWaitingForResponse() && this.processQueueOnSetcontextResponse(responseInfo);
    }
  }

  /**
   * Method to handle load failure from LS plugin manager.
   *
   * responseInfo - response information
   */
  private handlePluginManagerLoadFailure(responseInfo) {
    if (responseInfo.moduleName === this.APPLAUNCHER_CONFIG.MODULENAME) {
      this.handleAppLauncherPluginLoadFailureFromPM();
    }
  }

  // LineShaftPluginManager response handler
  LSPluginManagerResponseHandler(responseInfo) {
    if (
      responseInfo.topicName === 'RadDesktop.LS_PLUGIN_LOAD_RESPONSE' &&
      responseInfo.sourceName === 'LineShaftPluginManager'
    ) {
      if (responseInfo.resultValue === 'FAILURE') {
        log.error('LSPluginManagerResponseHandler - Load plugin failed');
        this.handlePluginManagerLoadFailure(responseInfo);
      }
    }
  }

  /**
   * Viewer, and CCOW setContext response handler.
   */
  LsPluginSetcontextResponseHandler(responseInfo) {
    // Check if viewer failed to launch
    // Launch viewer failed.
    if (responseInfo.topicName === 'RadDesktop.LS_PLUGIN_SETCONTEXT_RESPONSE') {
      this.handleSetcontextResponse(responseInfo);
    }
  }

  LsPluginLoadResponseHandler(responseInfo) {
    // AppLauncher plugin Load response
    if (responseInfo.topicName === 'LS_PLUGIN_LOAD_RESPONSE') {
      this.handleAppLauncherPluginLoadResponse(responseInfo);
    }
  }

  broadcastListeners() {
    // Lineshaft channel
    this.lineShaftHandlerService.channelObservers.onReady.subscribe(startLoad => {
      if (startLoad) {
        log.info('LineShaft registration started');
        this.loadPlugin();
      }
    });
    this.lineShaftHandlerService.channelObservers.onResponseMessage.subscribe(({ responseInfo, _responseXML }) => {
      if (responseInfo.sourceName === this.APPLAUNCHER_CONFIG.MODULENAME) {
        log.debug('LineShaft message processing', responseInfo);
        this.triggerProcessQueueOnResponse(responseInfo);
        this.LSPluginManagerResponseHandler(responseInfo);
        this.LsPluginSetcontextResponseHandler(responseInfo);
        this.LsPluginLoadResponseHandler(responseInfo);
      }
    });
    this.messageService.on('resetPlugin').subscribe(() => {
      log.info('LineShaft restart - Resetting AppLauncherPlugin status');
      this.integrationStatusService.setAppLauncherHealthStatus('init');
      this.integrationStatusService.setAppLauncherPluginStatus(false);
      this.AppLauncherMessageQueue.messageWaitingForResponse = undefined;
      this.AppLauncherMessageQueue.messageQueue = [];
    });
  }
}
